import { ChangeDetectorRef, Component, DestroyRef, Inject, inject, Input, OnChanges, OnInit, SimpleChanges, WritableSignal } from "@angular/core";
import { DialogService } from "primeng/dynamicdialog";
import { ViewReferralDialogComponent, ViewReferralDialogData } from "@shared/component/view-referral-dialog/view-referral-dialog.component";
import { CareCoordinationExactPage, ReferralListItem } from "../models/care-coordination-sub-widget.model";
import { MemberPrepopulatedData } from "@core/model";
import { CareCoordinationSubWidget } from "../models";
import { CareCoordinationExactPageService } from "@core/service/care-coordination-exact-page.service";
import { CareCoordinationSubWidgetTab } from "../models/care-coordination-sub-widget.enum";
import { LazyLoadEvent } from "primeng/api";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ODataService, TableColumn } from "@mynexus/mynexus-ui-lib";
import { clone } from "lodash-es";
import { Observable, finalize, timer } from "rxjs";
import { CareCoordinationService } from "@core/service/care-coordination.service";

@Component({
  selector: 'app-referrals-tab',
  templateUrl: './referrals-tab.component.html',
  styleUrls: ['./referrals-tab.component.scss'],
  providers: [DialogService],
  standalone: false
})
export class ReferralsTabComponent implements OnInit, OnChanges {
  private readonly dialogService = inject(DialogService);
  readonly defaultSortOrder = -1;
  readonly defaultSortField = 'createdDate';
  readonly rows = 5;
  copyOfItem: ReferralListItem;

  @Input() memberId!: string;
  @Input() tenantId: string;
  @Input() exactPageData: CareCoordinationExactPage = null;
  @Input() memberPrepopulatedData: MemberPrepopulatedData;

  cols = [];
  data: ReferralListItem[] = [];
  selectedItem!: ReferralListItem;
  firstPage = 0;

  defaultFilters = {
    sortField: this.defaultSortField,
    sortOrder: this.defaultSortOrder,
    first: this.firstPage,
    rows: this.rows
  };
  savedFilters: LazyLoadEvent = clone(this.defaultFilters);

  isLoading: boolean;
  totalRecords = 0;
  reasonReferencesMap: WritableSignal<Map<string, string>>;
  isExactPageItemFinded = false;

  constructor(
    @Inject('windowRef') private windowRef: Window,
    private readonly careCoordinationService: CareCoordinationService,
    private readonly oDataService: ODataService,
    private readonly ref: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) {
    this.cols = [
      new TableColumn('Referral ID', 'referenceId', '12rem', 'table-cell'),
      new TableColumn('Primary Referral Reason', 'statusReason', '13rem', 'table-cell'),
      new TableColumn('Date of Referral', 'createdOn', '13rem', 'table-cell'),
      new TableColumn('Submitted By', 'createdBy', '15rem', 'table-cell'),
      new TableColumn('Actions', null, '14rem', 'table-cell'),
    ];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { memberId, tenantId, oasisUpdated, exactPageData } = changes;

    if (memberId && (memberId.currentValue !== memberId.previousValue)) {
      this.loadReferrals(this.getFiltersData());
    }

    if (exactPageData?.currentValue && Object.keys(exactPageData?.currentValue).length > 0) {
      this.loadReferrals(this.getFiltersData(), true);
    }

    if (oasisUpdated?.currentValue) {
      this.onTableUpdated(true);
    }
  }

  ngOnInit(): void {
    this.loadReferrals(this.getFiltersData());
  }

  getFiltersData() {
    if (this.exactPageData?.pageNumber) {
      this.firstPage = this.exactPageData?.pageNumber * this.rows - this.rows;

      return {
        ...this.savedFilters,
        first: this.firstPage
      }
    }
    return this.defaultFilters;
  }

  handleLazyLoad(event: LazyLoadEvent) {
    this.savedFilters = {
      ...this.defaultFilters,
      ...event
    };
    this.loadReferrals(this.savedFilters);
  }

  loadReferrals(filter: LazyLoadEvent, handelExactPage: boolean = false) {
    this.isLoading = true;
    
    this.careCoordinationService.getCaseManagementReferrals(this.tenantId, this.memberId)
      .pipe(
        finalize(() => this.isLoading = false),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          const allItems = (response.items || []).map((item: any) => ({
            ...item,
            reason: this.parseReasons(item.reason)
          }));
          const first = filter?.first ?? 0;
          const rows = filter?.rows ?? this.rows;
          this.data = allItems.slice(first, first + rows);
          this.totalRecords = response.count || 0;
          this.ref.detectChanges();
        },
        error: (error) => {
          console.error('Error loading referrals:', error);
          this.data = [];
          this.totalRecords = 0;
        }
      });
  }

  onItemUpdated(event): void {
    if (event) {
      this.loadReferrals(this.savedFilters);
    }
  }

  onTableUpdated(event): void {
    if (event) {
      this.loadReferrals(this.savedFilters);
    }
  }

  openPreview(referralId: string, dateOfReferral: string): void {
    this.dialogService.open(ViewReferralDialogComponent, {
      header: 'Referral Form',
      width: '812px',
      closable: true,
      draggable: false,
      data: {
        tenantId: this.tenantId,
        memberId: this.memberId,
        subscriberNumber: this.memberPrepopulatedData?.subscriberNumber,
        referralId,
        dateOfReferral,
      } satisfies ViewReferralDialogData,
    });
  }

  private parseReasons(reason: string): string[] {
    if (!reason) return [];
    try {
      const parsed = JSON.parse(reason);
      return Array.isArray(parsed) ? parsed : [reason];
    } catch {
      return [reason];
    }
  }
}
